import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

export interface ProspectAlert {
  id: string;
  organization_id: string;
  crm_deal_id: string | null;
  contact_email: string;
  contact_name: string | null;
  post_id: string | null;
  short_link_id: string | null;
  engagement_type: 'click' | 'view' | 'share' | 'comment' | 'like';
  deal_stage: string | null;
  deal_amount: number | null;
  engagement_context: Record<string, any> | null;
  alerted_at: string;
  created_at: string;
}

export interface ProspectAlertWithPost extends ProspectAlert {
  posts?: {
    content: string;
    platform: string;
  } | null;
  crm_deals?: {
    deal_name: string;
    deal_stage: string | null;
    deal_amount: number | null;
  } | null;
}

export class ProspectAlertsService {
  private static readonly TABLE = 'prospect_alerts';
  private static logger = logger.child({ component: 'ProspectAlertsService' });

  /**
   * Get recent prospect alerts for an organization
   */
  static async getRecentAlerts(
    organizationId: string,
    limit: number = 50
  ): Promise<ProspectAlertWithPost[]> {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select(`
          *,
          posts(content, platform),
          crm_deals(deal_name, deal_stage, deal_amount)
        `)
        .eq('organization_id', organizationId)
        .order('alerted_at', { ascending: false })
        .limit(limit);

      if (error) {
        this.logger.error('Failed to fetch prospect alerts', error, { organizationId });
        throw error;
      }

      return data as ProspectAlertWithPost[];
    }, { operation: 'getRecentAlerts', table: this.TABLE });
  }

  /**
   * Get alerts for a specific deal
   */
  static async getAlertsForDeal(dealId: string): Promise<ProspectAlertWithPost[]> {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select(`
          *,
          posts(content, platform)
        `)
        .eq('crm_deal_id', dealId)
        .order('alerted_at', { ascending: false });

      if (error) {
        this.logger.error('Failed to fetch alerts for deal', error, { dealId });
        throw error;
      }

      return data as ProspectAlertWithPost[];
    }, { operation: 'getAlertsForDeal', table: this.TABLE });
  }

  /**
   * Get engagement summary by deal stage
   */
  static async getEngagementByStage(organizationId: string) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('deal_stage, engagement_type, deal_amount')
        .eq('organization_id', organizationId)
        .not('deal_stage', 'is', null);

      if (error) {
        this.logger.error('Failed to fetch engagement by stage', error, { organizationId });
        throw error;
      }

      // Aggregate by stage
      const stageMap = new Map<string, { count: number; totalValue: number }>();
      
      data?.forEach(alert => {
        if (!alert.deal_stage) return;
        
        const current = stageMap.get(alert.deal_stage) || { count: 0, totalValue: 0 };
        stageMap.set(alert.deal_stage, {
          count: current.count + 1,
          totalValue: current.totalValue + (alert.deal_amount || 0)
        });
      });

      return Array.from(stageMap.entries()).map(([stage, stats]) => ({
        stage,
        engagementCount: stats.count,
        totalDealValue: stats.totalValue
      }));
    }, { operation: 'getEngagementByStage', table: this.TABLE });
  }

  /**
   * Get top engaging prospects
   */
  static async getTopProspects(organizationId: string, limit: number = 10) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('contact_email, contact_name, deal_amount, engagement_type, crm_deal_id')
        .eq('organization_id', organizationId)
        .order('alerted_at', { ascending: false });

      if (error) {
        this.logger.error('Failed to fetch top prospects', error, { organizationId });
        throw error;
      }

      // Aggregate by email
      const prospectMap = new Map<string, {
        email: string;
        name: string | null;
        engagementCount: number;
        dealAmount: number | null;
        dealId: string | null;
      }>();

      data?.forEach(alert => {
        const current = prospectMap.get(alert.contact_email) || {
          email: alert.contact_email,
          name: alert.contact_name,
          engagementCount: 0,
          dealAmount: alert.deal_amount,
          dealId: alert.crm_deal_id
        };

        prospectMap.set(alert.contact_email, {
          ...current,
          engagementCount: current.engagementCount + 1
        });
      });

      return Array.from(prospectMap.values())
        .sort((a, b) => b.engagementCount - a.engagementCount)
        .slice(0, limit);
    }, { operation: 'getTopProspects', table: this.TABLE });
  }
}
